// Project Management rules shared by the Worker and the browser: vocabulary,
// the Day-0 credential checklists, week maths and the numbers shown on cards.
// Dates are YYYY-MM-DD strings; weeks start on Monday.

export const PROJECT_STATUSES = ['discovery', 'build', 'uat', 'live', 'on_hold']
export const PROJECT_STATUS_LABELS = {
  discovery: 'Discovery',
  build: 'Build',
  uat: 'UAT',
  live: 'Live',
  on_hold: 'On hold',
}

export const CRED_STATUSES = ['none', 'requested', 'received', 'verified', 'blocked']
export const CRED_STATUS_LABELS = {
  none: 'Not requested',
  requested: 'Requested',
  received: 'Received',
  verified: 'Verified',
  blocked: 'Blocked',
}
export const CRED_KINDS = ['api', 'user', 'network', 'sandbox']
export const CRED_KIND_LABELS = { api: 'API access', user: 'User account', network: 'Network', sandbox: 'Sandbox' }

export const SYSTEM_ROLES = ['source', 'target']
export const SYBILL_PULL_KEYS = ['summary', 'actions', 'recording']
export const MAX_PROJECT_SYSTEMS = 12
export const MAX_CREDENTIALS = 40
export const MAX_ACTIONS = 40
export const MAX_PROJECT_ACTIONS = 200

/** A new credential request is due this many days after it is added. */
export const CRED_DUE_DAYS = 5
/** Overview "Needs attention" lists open action items overdue by more than this. */
export const ATTENTION_OVERDUE_DAYS = 3
/** Open action items overdue by more than this are flagged "Long overdue". */
export const LONG_OVERDUE_DAYS = 14

/** The list we ask the client for when a system joins a project. */
const GENERIC = [
  ['api', 'Base URL and API credentials'],
  ['user', 'Service user with the integration role'],
  ['network', 'IP allowlist for Integrator'],
  ['sandbox', 'Test environment with sample data'],
]
const CHECKLISTS = {
  'p21-cloud': [
    ['api', 'P21 Cloud base URL'],
    ['api', 'OAuth client id and secret'],
    ['user', 'Service user with API role'],
    ['network', 'IP allowlist for Integrator'],
    ['sandbox', 'Test company with sample orders'],
  ],
  'axim-commerce': [
    ['api', 'Store URL and API key'],
    ['api', 'Webhook signing secret'],
    ['user', 'Admin user for catalogue setup'],
    ['sandbox', 'Staging storefront'],
  ],
  netsuite: [
    ['api', 'Account id and token-based auth keys'],
    ['user', 'Integration role and permissions'],
    ['network', 'Allow Integrator IPs'],
    ['sandbox', 'Sandbox account'],
  ],
}

/** @returns {Array<{kind: string, label: string}>} */
export function credentialTemplate(slug) {
  return (CHECKLISTS[slug] ?? GENERIC).map(([kind, label]) => ({ kind, label }))
}

// ---- Dates ----------------------------------------------------------------

export const todayIso = () => new Date().toISOString().slice(0, 10)

const toDate = (iso) => new Date(`${iso}T00:00:00Z`)
const toIso = (date) => date.toISOString().slice(0, 10)

export function addDays(iso, days) {
  const d = toDate(iso)
  d.setUTCDate(d.getUTCDate() + days)
  return toIso(d)
}

export function mondayOf(iso) {
  const d = toDate(iso)
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7))
  return toIso(d)
}

export const isIsoDate = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(toDate(value).getTime())

// ---- Derived numbers ------------------------------------------------------

/**
 * Every week from the one the project started in up to next week, newest
 * first. state: held | missed | due (this week, not logged) | upcoming.
 */
export function projectWeeks(project, today = todayIso()) {
  const thisWeek = mondayOf(today)
  const weeks = []
  for (let week = addDays(thisWeek, 7); week >= mondayOf(project.startOn); week = addDays(week, -7)) {
    const call = project.calls.find((c) => c.weekOf === week) ?? null
    const state = call ? 'held' : week > thisWeek ? 'upcoming' : week === thisWeek ? 'due' : 'missed'
    weeks.push({ weekOf: week, call, state })
  }
  return weeks
}

export const weekState = (project, today = todayIso()) =>
  projectWeeks(project, today).find((w) => w.weekOf === mondayOf(today))?.state ?? 'upcoming'

export const missedWeeks = (project, today) => projectWeeks(project, today).filter((w) => w.state === 'missed').length

export function credentialStats(project) {
  let total = 0
  let received = 0
  let blocked = 0
  for (const system of project.systems) {
    for (const cred of system.credentials) {
      total++
      if (cred.status === 'received' || cred.status === 'verified') received++
      if (cred.status === 'blocked') blocked++
    }
  }
  return { total, received, blocked }
}

/** Credentials that are blocked, or past due and still not received. */
export function credentialIssues(project, today = todayIso()) {
  const issues = []
  for (const system of project.systems) {
    for (const cred of system.credentials) {
      if (cred.status === 'blocked') issues.push({ system, cred, why: 'blocked' })
      else if (cred.dueOn && cred.dueOn < today && cred.status !== 'received' && cred.status !== 'verified') {
        issues.push({ system, cred, why: 'overdue' })
      }
    }
  }
  return issues
}

/** Open actions inside weekly calls. Ones moved to the Overview are counted there instead. */
export function openActions(project) {
  return project.calls.flatMap((call) => call.actions.filter((a) => !a.done && !a.moved).map((a) => ({ ...a, call })))
}

/** Whole days between two YYYY-MM-DD dates (a minus b). */
export const daysBetween = (a, b) => Math.round((toDate(a) - toDate(b)) / 86_400_000)

/** Days past due, or 0 when not overdue or no due date. */
export const daysOverdue = (dueOn, today = todayIso()) => (dueOn && dueOn < today ? daysBetween(today, dueOn) : 0)

export const openProjectActions = (project) => (project.actions ?? []).filter((a) => !a.done)

/** Overview action items: open first, most overdue first, then by due date, then undated. */
export function sortProjectActions(actions, today = todayIso()) {
  const rank = (a) => (a.done ? 3 : daysOverdue(a.dueOn, today) > 0 ? 0 : a.dueOn ? 1 : 2)
  return [...actions].sort((a, b) => {
    const r = rank(a) - rank(b)
    if (r) return r
    if (rank(a) === 0) return daysOverdue(b.dueOn, today) - daysOverdue(a.dueOn, today)
    if (rank(a) === 1) return a.dueOn < b.dueOn ? -1 : 1
    return (b.createdAt ?? 0) - (a.createdAt ?? 0)
  })
}

/** Open action items overdue by more than ATTENTION_OVERDUE_DAYS, most overdue first. */
export const attentionActions = (project, today = todayIso()) =>
  openProjectActions(project)
    .filter((a) => daysOverdue(a.dueOn, today) > ATTENTION_OVERDUE_DAYS)
    .sort((a, b) => daysOverdue(b.dueOn, today) - daysOverdue(a.dueOn, today))

export function lastCall(project) {
  return [...project.calls].sort((a, b) => (a.heldOn < b.heldOn ? 1 : -1))[0] ?? null
}

export const nextCallOn = (project) => lastCall(project)?.nextOn ?? null

/** Items for the Overview "Needs attention" panel. tone: bad | warn. */
export function attentionItems(project, today = todayIso()) {
  const items = []
  for (const { system, cred, why } of credentialIssues(project, today)) {
    items.push({
      tone: why === 'blocked' ? 'bad' : 'warn',
      tab: 'systems',
      text: `${system.name}: ${cred.label} ${why === 'blocked' ? `is blocked${cred.providedBy ? ` (waiting on ${cred.providedBy})` : ''}` : `was due ${cred.dueOn}`}.`,
    })
  }
  for (const a of attentionActions(project, today)) {
    const days = daysOverdue(a.dueOn, today)
    items.push({ tone: days > LONG_OVERDUE_DAYS ? 'bad' : 'warn', tab: 'overview', kind: 'action', text: `${a.subject} was due ${a.dueOn} (${days} days overdue).` })
  }
  const missed = missedWeeks(project, today)
  if (missed) items.push({ tone: 'warn', tab: 'calls', text: `${missed} weekly call${missed > 1 ? 's' : ''} missed since the project started.` })
  const overdue = openActions(project).filter((a) => a.dueOn && a.dueOn < today).length
  if (overdue) items.push({ tone: 'warn', tab: 'calls', text: `${overdue} call action${overdue > 1 ? 's are' : ' is'} overdue.` })
  if (!project.sybill.linked) items.push({ tone: 'warn', tab: 'sybill', text: 'Sybill is not linked to this project, so call notes are typed in by hand.' })
  if (!project.systems.length) items.push({ tone: 'warn', tab: 'systems', text: 'No systems added yet.' })
  return items
}

/** True when a project needs a red dot in the sidebar. */
export const needsAttention = (project, today) =>
  credentialIssues(project, today).some((i) => i.why === 'blocked') || missedWeeks(project, today) > 0
